<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="700px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="hrissecondary"
        block
        class="white--text"
        v-on="on"
      >
        Apply
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Leave On Behalf"
      text="Leave On Behalf"
      class="mb-0"
    > 
      <v-container>
        <v-row no-gutters>
          <v-col cols="12">
            <v-autocomplete
              v-model="employee"
              v-validate="'required'"
              :error-messages="errors.collect('employee')"
              :items="employees"
              data-vv-name="employee"
              data-vv-as="employee"
              item-text="person.name"
              item-id="employee_uid"
              return-object
              :loading="loading"
              label="Employee"
              clearable
              @change="loadWorkDays(employee)"
            />     
          </v-col>

          <v-col cols="12">
            <v-autocomplete
              v-model="leaveType"
              v-validate="'required'"
              :error-messages="errors.collect('leaveType')"
              :items="leaveTypes"
              data-vv-name="leaveType"
              data-vv-as="leave type"
              item-text="name"
              item-id="id"
              clearable
              return-object
              label="Leave Type"
            />     
          </v-col>
        </v-row>

        <v-row
          v-if="!_.isEmpty(leaveType) && leaveType.requires_document"
          no-gutters
        >
          <v-col cols="12">
            <helper-file-input
              v-model="file" 
              v-validate="'required'"
              :max-size-in-mb="2"
              :max-number="5"
              :multiple="true"
              data-vv-name="file"
              field-name="file"
              accept-type=".pdf"    
              extension="ext:pdf"         
            >
              <div 
                slot="activator"
                class="mb-2"
              >   
                <v-row>
                  <v-col
                    :cols="`${file ? 2 : 6}`"
                  >
                    <v-btn                    
                      :disabled="progressing"
                      class="mx-2"    
                    >
                      Add file
                    </v-btn>
                    <span> 
                      {{ file ? '' : 'No file chosen (.pdf only)' }}
                    </span>
                  </v-col>
                  <v-col
                    v-if="file"
                    cols="10"
                    class="px-2"
                  >
                    <span
                      v-for="(selectedFile, i) in file.selectedFiles"
                      :key="selectedFile.name"
                      class="font-weight-bold"
                    > {{ selectedFile.name }} {{ (i + 1) === file.selectedFiles.length ? '' : ',' }} </span>
                  </v-col>
                </v-row>
              </div>
            </helper-file-input>
            <span class="red--text text--lighten-1 caption">{{ errors.first('file') }}</span>
          </v-col>
        </v-row>

        <v-row
          v-if="!_.isEmpty(leaveType)"
          no-gutters
        >
          <v-col cols="6">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="startDate"
                  v-validate="'required'"
                  label="Start Date"
                  prepend-icon="event"
                  :error-messages="errors.collect('start_date')"
                  data-vv-name="start_date"  
                  data-vv-as="start date"      
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="startDate"
                :min="earliestDate"
                @input="startDateChanged"
              />
            </v-menu>
          </v-col>

          <v-col cols="6">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="endDate"
                  v-validate="'required'"
                  :disabled="leaveType.is_continuous ? true : false"
                  label="End Date"
                  prepend-icon="event"
                  :error-messages="errors.collect('end_date')"
                  data-vv-name="end_date"  
                  data-vv-as="end date"
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="endDate"                  
                :min="startDate ? startDate : earliestDate"
                :max="latestDate"
                @input="endDateChanged"
              />
            </v-menu>
          </v-col>
        </v-row>

        <v-row no-gutters>
          <v-col cols="12">
            <i style="font-size:12px">{{ leaveHint }}</i>
          </v-col>
        </v-row>

        <v-card
          v-if="datesSelected && !leaveType.is_continuous"
          class="elevation-1"
          max-height="280px"
          data-simplebar
        >            
          <v-container class="pa-0">
            <v-progress-linear
              v-if="leaveDays.length < 1"
              indeterminate
              color="primary"
            />
            <v-row
              v-for="(item, i) in leaveDays"
              v-else
              :key="i"
              justify="center"
              align="center"
              no-gutters
            >
              <v-col              
                cols="12"
                md="4"
              >
                <span class="font-weight-medium">
                  {{ item.date }} ({{ $moment(item.date).format('ddd') }}) :
                </span>
              </v-col>
              <v-col              
                cols="12"
                md="7"
              >
                <v-radio-group
                  v-model="leaveDays[i].leave_session_id"
                  row
                  :disabled="leaveDays[i].disabled"
                >
                  <v-radio
                    v-for="session in leaveSessions"
                    :key="session.id"
                    :label="session.name"
                    :value="session.id"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
          </v-container>
        </v-card>

        <v-row v-if="(!_.isEmpty(leaveType) && leaveType.requires_reason && showReason)||backDatedLeave">
          <v-col cols="12">
            <v-textarea
              v-model="reason"
              v-validate="'required|max:5000'"
              :rows="2"
              :error-messages="errors.collect('reason')"
              data-vv-name="reason"        
              label="Reason"
            />
          </v-col>
        </v-row> 
      </v-container>

      <v-card-actions>
        <div>
          <p
            v-if="datesSelected"
            class="font-weight-bold"
          >
            Leave Requested: {{ leaveDaysRequested }} Day(s)
          </p>
          <p class="red--text text--lighten-1 caption">
            {{ errorMessage }}
          </p>
        </div>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"
          class="white--text"
          @click="submitForm"
        >
          Create
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    components: {
      HelperFileInput: () => import('@/components/helper/FileInput'),
    },
    $_veeValidate: {
      validator: 'new', 
    },
    data () {
      return {
        progressing: false,
        loading: false,
        dialog: null,
        file: null,
        employee: null,
        leaveType: null,
        startDateMenu: null,
        startDate: null,
        endDateMenu: null,
        endDate: null,
        reason: null,
        employees: [],
        holidayDetails: [],
        workDays: [],
        leaveDays: [],
        leaveSessions: [],
        workSession: {
          '2': 2,
          '3': 3,
          '4': 4,
          '1': 4,
        },
      }
    },

    computed: {
      leaveDaysRequested () {
        return this.leaveDays.reduce((accumulator, leaveDay) => {
          const day = leaveDay.leave_session_id === 4 ? 1 : 0.5
          
          return accumulator + day
        }, 0)
      }, 
      errorMessage () {
        if (!this.selectedEntitlement) return
        let carriedOverDays = this.carriedOverEntitlementDays ? this.carriedOverEntitlementDays.carried_forward_days : 0
        if (this.selectedEntitlement.available_days - carriedOverDays < this.leaveDaysRequested) return '*Not enough available leaves'
            
        return ''
      },  
      leaveHint () {
        let carriedOverDays = this.carriedOverEntitlementDays ? this.carriedOverEntitlementDays.carried_forward_days : 0
        return _.isEmpty(this.startDate) ? '' : `*Leave Available: ${this.roundedTwoDecimal(this.selectedEntitlement.available_days - carriedOverDays)} Day(s)` 
      },
      leaveTypes () {
        if (!this.employee) return []

        let leaveTypes = []

        this.employee.leave_entitlements.map(entitlement => {
          leaveTypes.push(entitlement.leave_type)
        })

        return _.orderBy(_.uniqBy(leaveTypes, 'id'), ['name'], ['asc'])
      },
      earliestDate () {
        return this.$moment().startOf('year').format('YYYY-MM-DD')
      },
      latestDate () {
        return this.$moment(this.startDate).endOf('year').format('YYYY-MM-DD')
      },
      holidayDates () {
        return this.holidayDetails.filter(holidayDetail => {
          if (holidayDetail.holiday.is_national) return true
          
          if (holidayDetail.holiday_states.length > 0) {
            return holidayDetail.holiday_states.some(state => state.state_id === this.employee.city.state_id && state.department_id === this.employee.department_id) 
          }
          return false
        })
          .map(holidayDetail => holidayDetail.date)
      },
      carriedOverEntitlementDays () {
        if (!this.startDate) return

        return this.employee.leave_entitlements.find(entitlement => entitlement.leave_type_id === this.leaveType.id && entitlement.year === (parseInt(this.startDate.substring(0, 4)) + 1))
      },
      selectedEntitlement () {
        if (!this.startDate) return
  
        return this.employee.leave_entitlements.find(entitlement => entitlement.leave_type_id === this.leaveType.id && entitlement.year === parseInt(this.startDate.substring(0, 4)))
      },
      datesSelected () {
        return this.startDate && this.endDate && this.noOfSelectedDays > 0
      }, 
      noOfSelectedDays () {
        const startDate = this.$moment(this.startDate)
        const endDate = this.$moment(this.endDate)

        return endDate.diff(startDate, 'days') + 1 // includes day 1
      },   
      workingDays () {
        return this.workDays.filter(workDay => workDay.work_session.start_time !== workDay.work_session.end_time)
      },  

      showReason () {
        if (!this.leaveType || !this.startDate) return false
        
        return this.$moment(this.startDate).diff(this.$moment(), 'days') + 1 <= this.leaveType.minimum_days_reason_required
      },
      backDatedLeave () {
        if (!this.leaveType || !this.startDate) return false
        
        if (this.$moment(this.startDate).diff(this.$moment(), 'days') + 1 <= 0) {
          return true
        }
        return false
      },
    },

    watch: {
      dialog: function (value) {
        if (!value) return       
        this.loadEmployeeOnBehalfLeaveEntitlement()
        this.loadLeaveSessions()
      },
    },

    methods: {
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            if (this.errorMessage) return          

            this.progressing = true

            this.$api.onBehalfLeaveApplications().create(this.prepareFormData())
              .then(response => {
                this.$emit('created')
                this.$snack({ message: 'Leave Applied' })

                this.progressing = false
                this.dialog = false
              })
              .catch(() => {
                this.progressing = false
              })
          })
      },

      prepareFormData () {
        if (this.file) {
          this.file.formData.set('employee', this.employee.employee_uid)
          this.file.formData.set('leave_type', this.leaveType.id)
          this.file.formData.set('start_date', this.startDate)
          this.file.formData.set('end_date', this.endDate)
          this.file.formData.set('reason', this.reason ? this.reason : 'Personal Matters - by System')
          this.file.formData.set('leave_days', JSON.stringify(this.leaveDays))

          return this.file.formData
        }

        let formData = new FormData()

        formData.append('employee', this.employee.employee_uid)
        formData.append('leave_type', this.leaveType.id)
        formData.append('start_date', this.startDate)
        formData.append('end_date', this.endDate)
        formData.append('reason', this.reason ? this.reason : 'Personal Matters - by System')
        formData.append('leave_days', JSON.stringify(this.leaveDays))

        return formData
      },

      loadLeaveSessions () {
        this.$api.leaveSessions().list()
          .then(res => {
            this.leaveSessions = res.data.filter(session => session.name.toUpperCase() !== 'NONE')
          })
      },

      roundedTwoDecimal (value) {
        return Math.round(value * 100, 0) / 100
      },
      loadEmployeeOnBehalfLeaveEntitlement () {
        this.loading = true

        this.$api.employeeOnBehalfLeaveEntitlements().list()
          .then(res => {
            this.employees = _.orderBy(res.data, ['person.name'], ['asc'])       

            this.loading = false
          })
      },

      loadWorkDays (employee) {
        this.$api.employeeWorkdays().list(employee.employee_uid)
          .then(res => {
            this.workDays = res.data
          })
      },

      startDateChanged () {
        Promise.all([
          this.loadHolidays(),
        ]).then(([holiday]) => {
          this.holidayDetails = holiday.data
          this.clearLeaveDays()
          this.computeLeaveDates()
          this.computeLeaveDays()
        })
      },

      endDateChanged () {
        this.endDateMenu = false

        this.clearLeaveDays()
        this.computeLeaveDays()
      },
      
      computeLeaveDates () {
        this.startDateMenu = false
        let totalDays = this.$moment(this.startDate).add(1, 'days').diff(this.$moment(this.startDate), 'days') 
        this.endDate = this.getComputedEndDate(totalDays)
        
        if (this.leaveType.is_continuous) {
          let carriedOverDays = this.carriedOverEntitlementDays ? this.carriedOverEntitlementDays.carried_forward_days : 0
   
          let totalDays = this.selectedEntitlement.available_days - carriedOverDays - 1
          this.endDate = this.getComputedEndDate(totalDays)
        }
      },

      computeLeaveDays () {
        if (!this.datesSelected) return 
        
        let leaveDates = this.getLeaveDates(this.startDate, this.endDate, this.noOfSelectedDays)

        leaveDates.filter(leaveDate => {
          const isTodayHoliday = this.holidayDates.includes(leaveDate)

          if (this.leaveType.is_continuous || this.leaveType.includes_rest_day || this.leaveType.includes_off_day || this.leaveType.includes_public_holiday) {
            // Sunday
            let isTodayRestDay = false
            if (this.$moment(leaveDate).day() === 0) isTodayRestDay = true
            if (isTodayRestDay && this.leaveType.includes_rest_day) return leaveDate 

            // Saturday
            let isTodayOffDay = false
            if (this.$moment(leaveDate).day() === 6) isTodayOffDay = true
            if (isTodayOffDay && this.leaveType.includes_off_day) return leaveDate
            
            if (isTodayHoliday && this.leaveType.includes_public_holiday) return leaveDate

            // Holiday
            if (!isTodayRestDay && !isTodayOffDay && !isTodayHoliday) return leaveDate
          } else {
            // leave date filter by work day

            const isWorkDay = this.workingDays.find(workingDay => {
              const isoWorkingDay = workingDay.day === 7 ? 0 : workingDay.day
            
              return isoWorkingDay === this.$moment(leaveDate).day()
            })
            
            if (isWorkDay && !isTodayHoliday) return leaveDate
          }
        }).map((leaveDate, i) => {
          if (this.leaveType.is_continuous || this.leaveType.includes_rest_day || this.leaveType.includes_off_day || this.leaveType.includes_public_holiday) {
            this.leaveDays.push({
              date: leaveDate,
              leave_session_id: 4,
            })
          } else {
            this.workDays.map(item => {
              const isoWorkingDay = item.work_session.day === 7 ? 0 : item.work_session.day
            
              if (this.$moment(leaveDate).day() === isoWorkingDay) {
                this.leaveDays.push({
                  date: leaveDate,
                  leave_session_id: 4,
                })
              }
            })
          }
        })      
      },

      clearLeaveDays () {
        this.leaveDays = []
      },   

      loadHolidays () {
        return this.$api.hrisHolidayDetails().list('?filter[year]=' + this.startDate.substring(0, 4))
      },

      getComputedEndDate (totalDays) {
        let getDate = this.$moment(this.startDate)
        for (var i = 0; i <= totalDays; i++) {
          getDate = i < 0 ? this.$moment(this.startDate) : this.$moment(this.startDate).add(i, 'days').format('YYYY-MM-DD')
          const isTodayHoliday = this.holidayDates.includes(this.$moment(getDate).format('YYYY-MM-DD'))
          if ((!this.leaveType.includes_off_day && this.$moment(getDate).day() === 6) || 
            (!this.leaveType.includes_rest_day && this.$moment(getDate).day() === 0) ||
            (!this.leaveType.includes_public_holiday && isTodayHoliday)) {
            totalDays = totalDays + 1
          }
        }

        return this.$moment(getDate).format('YYYY-MM-DD')
      },

      getLeaveDates (selectedStartDate, selectedEndDate, interval) {
        let dateArray = []
        let startDate = this.$moment(selectedStartDate)
        const endDate = this.$moment(selectedEndDate)

        while (startDate <= endDate) {
          dateArray.push(this.$moment(startDate).format('YYYY-MM-DD'))

          startDate = this.$moment(startDate).add(1, 'days')
        }
        
        return dateArray
      },  
    },
  }
</script>
